/**
 * Handles click events on share buttons by generating and opening a share URL for the specified service.
 * Also sends analytics event if analytics function is available.
 * @this {Element} The share button element that was clicked
 * @returns {void}
 */
function handleShareClick() {
	const service = this.getAttribute('data-service');
	const capitalizedService = service.charAt(0).toUpperCase() + service.slice(1);
	const platform = service === 'x' ? 'X' : service;

	const text = this.parentElement.getAttribute('data-text');
	const title = this.parentElement.getAttribute('data-title');
	const content_type = this.parentElement.hasAttribute('data-content-type');

	let sharedUrl = new URL(this.parentElement.getAttribute('data-url') ?? window.location.href);
	sharedUrl.searchParams.set('utm_source', service);
	sharedUrl.searchParams.set('utm_medium', 'organic_social');
	sharedUrl.searchParams.set('utm_campaign', 'house');

	if (service === 'copylink') {
		navigator.clipboard
			.writeText(sharedUrl.toString())
			.then(() => {
				window.CusaToastNotification?.create({
					type: 'success',
					title: 'Link copied!',
					message: 'The link has been copied to your clipboard.'
				});
			})
			.catch(() => {
				window.CusaToastNotification?.create({
					type: 'error',
					title: 'Copy failed',
					message: 'Unable to copy the link. Please try again.'
				});
			});
		trackShareEvent(platform, content_type);
		return;
	}

	const shareUrl = getShareUrl(sharedUrl.toString(), service, title, text);

	if (shareUrl) {
		spawnShare(shareUrl, capitalizedService);
		trackShareEvent(platform, content_type);
	} else {
		console.error(`Cannot share on ${capitalizedService}; share URL for ${sharedUrl.toString()} not found`);
	}
}

/**
 * Fires a share analytics event for the given platform if the analytics helper is available.
 * @param {string} platform - Platform name as sent to analytics (e.g. 'facebook', 'X', 'copylink')
 * @param {boolean} content_type - Whether the share container has a data-content-type attribute set
 * @returns {void}
 */
function trackShareEvent(platform, content_type) {
	if ('function' !== typeof sendAnalyticsEvent) return;
	sendAnalyticsEvent('share_' + platform, {
		content_type,
		item_id: 'function' === typeof getSlug ? getSlug(window.location.href) : ''
	});
}

/**
 * Opens the share URL in a small centred popup window, or navigates directly for email shares.
 * @param {string} shareUrl - The fully-formed share destination URL
 * @param {string} capitalizedService - Display name of the service (e.g. 'Facebook', 'Email')
 * @returns {void}
 */
function spawnShare(shareUrl, capitalizedService) {
	if ('Email' !== capitalizedService) {
		window.open(
			shareUrl,
			`Share on ${capitalizedService}`,
			'height=450, width=550, top=' + (window.innerHeight - 450) / 2 + ', left=' + (window.innerWidth - 550) / 2
		);
	} else {
		window.location = shareUrl;
	}
}

/**
 * Builds the service-specific share URL from the canonical URL, title, and text.
 * All string params are expected to be decoded — this function encodes them internally.
 * @param {string} url - Canonical page URL
 * @param {'facebook'|'twitter'|'x'|'pinterest'|'email'} service - Target share service
 * @param {string} title - Page title for use in share text
 * @param {string} text - Page excerpt or description for use in share text
 * @returns {string} Complete share URL, or empty string for unsupported services
 */
function getShareUrl(url, service, title, text) {
	url = encodeURIComponent(url.trim());
	title = encodeURIComponent(title.trim());
	text = encodeURIComponent(text.trim());
	switch (service) {
		case 'facebook':
			return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
		case 'twitter':
		case 'x':
			// carriage returns between title, text, and url
			return `https://x.com/intent/post?url=${url}&text=${title + '%0A%0A' + text + '%0A%0A'}`;
		case 'pinterest':
			return `https://pinterest.com/pin/create/button/?url=${url}&description=${title}`;
		case 'email':
			return `mailto:?subject=${title}&body=${text}%0A%0A${url}`;
		default:
			return '';
	}
}

/**
 * Attaches click handlers to every share button within the given root.
 * Safe to call multiple times — once on DOMContentLoaded and again whenever
 * share buttons are injected dynamically (e.g. when a funnel card is revealed).
 * @param {Document|Element} [root=document]
 * @returns {void}
 */
function initializeShareButtons(root = document) {
	root.querySelectorAll('.shares button').forEach((share) => share.addEventListener('click', handleShareClick));
}

window.addEventListener('DOMContentLoaded', () => initializeShareButtons());
